import React, { useEffect, useState, useMemo } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import { Calendar, Clock, User, Mail, Phone, CheckCircle2, XCircle, UserCheck, FileText } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  pending_payment: { label: "Attente paiement", color: "amber" },
  pending: { label: "À valider", color: "orange" },
  confirmed: { label: "Confirmé", color: "emerald" },
  completed: { label: "Terminé", color: "slate" },
  cancelled: { label: "Annulé", color: "red" },
  no_show: { label: "Absent", color: "rose" },
};

const STATUS_STYLES = {
  amber: "bg-amber-50 text-amber-700 border-amber-200",
  orange: "bg-orange-50 text-orange-700 border-orange-200",
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  slate: "bg-slate-100 text-slate-700 border-slate-200",
  red: "bg-red-50 text-red-700 border-red-200",
  rose: "bg-rose-50 text-rose-700 border-rose-200",
};

function formatDateFr(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("fr-FR", { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

export default function BookingsAdmin() {
  const [items, setItems] = useState([]);
  const [team, setTeam] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [notesOpen, setNotesOpen] = useState(null); // booking id

  const load = async () => {
    setLoading(true);
    try {
      const q = statusFilter ? `?status=${statusFilter}` : "";
      const [bs, tm] = await Promise.all([
        api.get(`/admin/bookings${q}`),
        api.get("/admin/team").catch(() => ({ data: [] })),
      ]);
      setItems(bs.data || []);
      setTeam(tm.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [statusFilter]); // eslint-disable-line

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/admin/bookings/${id}`, { status });
      toast.success("Statut mis à jour");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };
  const assignTeam = async (id, tmid) => {
    try {
      await api.put(`/admin/bookings/${id}`, { assigned_team_member_id: tmid || null });
      toast.success("Affectation enregistrée");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };
  const saveNotes = async (id, notes) => {
    try {
      await api.put(`/admin/bookings/${id}`, { admin_notes: notes });
      toast.success("Note enregistrée");
      setNotesOpen(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const counts = useMemo(() => {
    const c = { all: items.length };
    for (const b of items) c[b.status] = (c[b.status] || 0) + 1;
    return c;
  }, [items]);

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="bookings-admin">
      <header className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--brand-sky)] font-semibold mb-1">Réservation</div>
          <h1 className="font-display text-3xl font-bold text-[color:var(--brand-navy)]">Réservations</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Gérez les demandes reçues, validez, affectez un membre de l'équipe.</p>
        </div>
        <div className="w-56">
          <Select value={statusFilter || "all"} onValueChange={v => setStatusFilter(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="booking-status-filter"><SelectValue placeholder="Tous statuts" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous statuts ({counts.all || 0})</SelectItem>
              {Object.entries(STATUS_META).map(([k, m]) => (
                <SelectItem key={k} value={k}>{m.label} ({counts[k] || 0})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </header>

      {loading ? (
        <div className="py-12 text-center text-sm text-[hsl(var(--muted-foreground))]">Chargement…</div>
      ) : items.length === 0 ? (
        <Card className="p-12 text-center">
          <Calendar className="w-10 h-10 mx-auto text-[hsl(var(--muted-foreground))]/40 mb-2" />
          <div className="font-semibold text-[color:var(--brand-navy)]">Aucune réservation {statusFilter ? "avec ce statut" : "pour le moment"}</div>
          <div className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Les nouvelles demandes apparaîtront ici.</div>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map(b => {
            const meta = STATUS_META[b.status] || { label: b.status, color: "slate" };
            const tm = b.assigned_team_member;
            return (
              <Card key={b.id} className="p-4" data-testid={`booking-row-${b.id.slice(0, 8)}`}>
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  <div className="flex items-center gap-3 lg:w-64 shrink-0">
                    <div className="w-12 h-12 rounded-lg bg-[color:var(--brand-sky)]/15 flex items-center justify-center text-[color:var(--brand-sky)]">
                      <Calendar className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-sm text-[color:var(--brand-navy)] truncate">{b.customer_name}</div>
                      <div className="text-xs text-[hsl(var(--muted-foreground))] truncate"><Mail className="w-3 h-3 inline mr-1" />{b.customer_email}</div>
                      {b.customer_phone && (
                        <div className="text-xs text-[hsl(var(--muted-foreground))] truncate"><Phone className="w-3 h-3 inline mr-1" />{b.customer_phone}</div>
                      )}
                    </div>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-sm font-semibold text-[color:var(--brand-navy)]">
                      <Clock className="w-3.5 h-3.5" />{formatDateFr(b.scheduled_at)}
                      <span className="text-[hsl(var(--muted-foreground))]">· {b.total_duration_min} min</span>
                    </div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
                      {(b.items || []).map(it => it.title).join(" · ")}
                    </div>
                    <div className="flex items-center gap-2 text-xs mt-1">
                      <span className="font-mono">{(b.total_price_cents / 100).toFixed(2)} €</span>
                      {b.payment_mode !== "free" && (
                        <>
                          <span className="text-[hsl(var(--muted-foreground))]">·</span>
                          <Badge variant="outline" className="text-[9px] uppercase tracking-wider">
                            {b.payment_mode === "deposit" ? `Acompte ${(b.deposit_cents / 100).toFixed(2)} €` : "Plein tarif"}
                          </Badge>
                          <Badge variant="outline" className={`text-[9px] uppercase tracking-wider ${b.payment_status === "paid" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100"}`}>
                            {b.payment_status === "paid" ? "Payé" : "Non payé"}
                          </Badge>
                        </>
                      )}
                    </div>
                    {b.customer_notes && (
                      <div className="mt-1 text-xs text-[hsl(var(--muted-foreground))] italic">"{b.customer_notes}"</div>
                    )}
                  </div>

                  <div className="flex flex-col gap-2 lg:w-72 shrink-0">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border ${STATUS_STYLES[meta.color]}`}>● {meta.label}</span>
                    </div>
                    <Select value={b.status} onValueChange={v => updateStatus(b.id, v)}>
                      <SelectTrigger className="h-8 text-xs" data-testid={`booking-status-${b.id.slice(0, 8)}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(STATUS_META).map(([k, m]) => (
                          <SelectItem key={k} value={k}>{m.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={b.assigned_team_member_id || "none"} onValueChange={v => assignTeam(b.id, v === "none" ? null : v)}>
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue placeholder="Aucun prestataire">
                          {tm ? (
                            <span className="flex items-center gap-1.5"><UserCheck className="w-3 h-3" />{tm.first_name} {tm.last_name || ""}</span>
                          ) : "— Aucun prestataire —"}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— Aucun prestataire —</SelectItem>
                        {team.map(t => (
                          <SelectItem key={t.id} value={t.id}>{t.first_name} {t.last_name || ""}{t.role ? ` · ${t.role}` : ""}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button size="sm" variant="outline" onClick={() => setNotesOpen(b.id)} className="h-8 text-xs">
                      <FileText className="w-3 h-3 mr-1" />{b.admin_notes ? "Notes ✓" : "Ajouter notes"}
                    </Button>
                  </div>
                </div>

                {notesOpen === b.id && (
                  <div className="mt-4 pt-4 border-t">
                    <Textarea rows={3} defaultValue={b.admin_notes || ""} id={`notes-${b.id}`} />
                    <div className="flex gap-2 mt-2">
                      <Button size="sm" onClick={() => saveNotes(b.id, document.getElementById(`notes-${b.id}`).value)} className="bg-[color:var(--brand-navy)]">Enregistrer</Button>
                      <Button size="sm" variant="outline" onClick={() => setNotesOpen(null)}>Annuler</Button>
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
